/********************************************************************
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import { ComponentChildren, FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { VComponent, classes } from '@adobe-commerce/elsie/lib';
import '@adobe-commerce/elsie/components/Button/Button.css';

export interface ButtonProps
  extends Omit<
    HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
    'size' | 'icon'
  > {
  variant?: 'primary' | 'secondary' | 'tertiary';
  size?: 'medium' | 'large';
  children?: ComponentChildren;
  icon?: VNode<HTMLAttributes<SVGSVGElement>>;
  disabled?: boolean;
  active?: boolean;
  activeChildren?: ComponentChildren;
  activeIcon?: VNode<HTMLAttributes<SVGSVGElement>>;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
}

export const Button: FunctionComponent<ButtonProps> = ({
  value,
  variant = 'primary',
  size = 'medium',
  icon,
  className,
  children,
  disabled = false,
  active = false,
  activeChildren,
  activeIcon,
  href,
  ...props
}) => {
  let buttonType = 'dropin-button';

  if (
    (icon && !children) ||
    (icon && active && !activeChildren) ||
    (!icon && active && activeIcon)
  ) {
    buttonType = 'dropin-iconButton';
  }

  if (active && activeChildren) {
    buttonType = 'dropin-button';
  }

  className = classes([
    buttonType,
    `${buttonType}--${size}`,
    `${buttonType}--${variant}`,
    [`${buttonType}--${variant}--disabled`, disabled],
    children && icon && `${buttonType}--with-icon`,
    !children && activeChildren && icon && `${buttonType}--with-icon`,
    active && activeIcon && `${buttonType}--with-icon`,
    className,
  ]);

  const iconClassName = classes([
    'dropin-button-icon',
    `dropin-button-icon--${variant}`,
    [`dropin-button-icon--${variant}--disabled`, disabled],
    icon?.props.className,
  ]);

  const attributes = href
    ? {
        node: <a />,
        role: 'link',
        href,
        ...props,
        disabled,
        active,
        onKeyDown: (event: KeyboardEvent) => {
          if (disabled) {
            event.preventDefault();
          }
        },
        tabIndex: disabled ? -1 : 0,
      }
    : {
        node: <button />,
        role: 'button',
        ...props,
        value,
        disabled,
        active,
      };

  return (
    <VComponent {...attributes} className={className}>
      {icon && !active && <VComponent node={icon} className={iconClassName} />}

      {activeIcon && active && (
        <VComponent node={activeIcon} className={iconClassName} />
      )}

      {children &&
        !active &&
        (typeof children === 'string' ? <span>{children}</span> : children)}

      {active &&
        activeChildren &&
        (typeof activeChildren === 'string' ? (
          <span>{activeChildren}</span>
        ) : (
          activeChildren
        ))}
    </VComponent>
  );
};
